import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const [tab, setTab] = useState("atleta"); // 'atleta' | 'tecnico' | 'code'
  const [mode, setMode] = useState("login"); // for atleta: 'login' | 'signup'
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [code, setCode] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const goAfterAuth = (user) => {
    if (user.role === "tecnico") navigate("/dashboard");
    else navigate("/passaporte");
  };

  const handleGoogle = () => {
    sessionStorage.setItem("login_role", "tecnico");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAtletaSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password.trim()) {
      toast.error("Preencha email e senha");
      return;
    }
    if (mode === "signup" && !form.name.trim()) {
      toast.error("Informe seu nome");
      return;
    }
    setSubmitting(true);
    try {
      const endpoint = mode === "signup" ? "/auth/register" : "/auth/login";
      const payload = mode === "signup"
        ? { email: form.email.trim().toLowerCase(), password: form.password, name: form.name.trim() }
        : { email: form.email.trim().toLowerCase(), password: form.password };
      const res = await api.post(endpoint, payload);
      const user = res.data.user;
      setUser(user);
      toast.success(mode === "signup" ? `Bem-vinda, ${user.name}!` : "Login realizado");
      goAfterAuth(user);
    } catch (err) {
      const msg = err.response?.data?.detail || "Falha na autenticação";
      toast.error(typeof msg === "string" ? msg : "Falha na autenticação");
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!code.trim() || !inviteName.trim()) {
      toast.error("Preencha código e nome");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/auth/invite", { code: code.trim().toUpperCase(), name: inviteName.trim() });
      const user = res.data.user;
      setUser(user);
      toast.success(`Bem-vinda, ${user.name}!`);
      goAfterAuth(user);
    } catch (err) {
      const msg = err.response?.data?.detail || "Falha ao validar código";
      toast.error(typeof msg === "string" ? msg : "Falha ao validar código");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1590227632180-80a3bf110871?crop=entropy&cs=srgb&fm=jpg&q=80"
          alt=""
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/40" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-12 min-h-screen flex flex-col">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-[#FF3B30] flex items-center justify-center font-display font-black text-white text-xl">PA</div>
          <div>
            <div className="font-display font-black text-xl uppercase tracking-tight leading-none">Passaporte do Atleta</div>
            <div className="text-[10px] text-zinc-400 uppercase tracking-[0.3em]">Performance Analytics</div>
          </div>
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mt-8 lg:mt-0">
          <div className="space-y-6 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-zinc-800 bg-zinc-900/60">
              <span className="w-2 h-2 rounded-full bg-[#22C55E] animate-pulse" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 font-bold">Plataforma para comissão técnica</span>
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl uppercase tracking-tight leading-[0.9]">
              Dados de arremesso<br />
              <span className="text-[#FF3B30]">em tempo real.</span>
            </h1>
            <p className="text-zinc-400 text-lg max-w-xl">
              Mapa de calor por zona da quadra, ranking de atletas, evolução por período e relatórios em PDF.
            </p>
            <div className="grid grid-cols-3 gap-4 max-w-md">
              {[
                { c: "#22C55E", n: "70-100%" },
                { c: "#EAB308", n: "55-69%" },
                { c: "#F97316", n: "40-54%" },
                { c: "#EF4444", n: "< 40%" },
              ].map((i) => (
                <div key={i.n} className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm" style={{ background: i.c }} />
                  <span className="text-xs text-zinc-300 font-bold uppercase tracking-wider">{i.n}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#141414] border border-zinc-800 rounded-md p-8 fade-up" data-testid="login-card">
            <h2 className="font-display font-bold text-3xl uppercase tracking-tight">Acessar</h2>

            <div className="grid grid-cols-2 gap-1 mt-6 bg-[#0A0A0A] border border-zinc-800 rounded-sm p-1">
              <button
                onClick={() => setTab("atleta")}
                data-testid="tab-atleta"
                className={`py-2 text-xs font-bold uppercase tracking-wide rounded-sm transition-colors ${
                  tab === "atleta" ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:text-white"
                }`}
              >
                Atleta
              </button>
              <button
                onClick={() => setTab("tecnico")}
                data-testid="tab-tecnico"
                className={`py-2 text-xs font-bold uppercase tracking-wide rounded-sm transition-colors ${
                  tab === "tecnico" ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:text-white"
                }`}
              >
                Técnico
              </button>
            </div>

            {tab === "atleta" && (
              <div className="mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setMode("login")}
                    data-testid="mode-login"
                    className={`text-xs font-bold uppercase tracking-widest pb-1 border-b-2 transition-colors ${
                      mode === "login" ? "border-[#FF3B30] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    Entrar
                  </button>
                  <span className="text-zinc-700">•</span>
                  <button
                    type="button"
                    onClick={() => setMode("signup")}
                    data-testid="mode-signup"
                    className={`text-xs font-bold uppercase tracking-widest pb-1 border-b-2 transition-colors ${
                      mode === "signup" ? "border-[#FF3B30] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    Criar Conta
                  </button>
                </div>

                <form onSubmit={handleAtletaSubmit} className="space-y-3">
                  {mode === "signup" && (
                    <div>
                      <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Nome</label>
                      <Input
                        data-testid="atleta-name-input"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Seu nome completo"
                        className="mt-2 bg-[#0A0A0A] border-zinc-800 text-white"
                      />
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Email</label>
                    <Input
                      data-testid="atleta-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="voce@email.com"
                      className="mt-2 bg-[#0A0A0A] border-zinc-800 text-white"
                      autoComplete="email"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Senha</label>
                    <Input
                      data-testid="atleta-password-input"
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder={mode === "signup" ? "Mínimo 6 caracteres" : "Sua senha"}
                      className="mt-2 bg-[#0A0A0A] border-zinc-800 text-white"
                      autoComplete={mode === "signup" ? "new-password" : "current-password"}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="atleta-submit-btn"
                    className="w-full bg-[#FF3B30] hover:bg-red-600 text-white font-bold uppercase tracking-wide py-6 rounded-sm text-base disabled:opacity-50"
                  >
                    {submitting ? "Aguarde..." : mode === "signup" ? "Criar Conta" : "Entrar"}
                  </Button>
                </form>

                <div className="mt-6 pt-4 border-t border-zinc-800">
                  <button
                    onClick={() => setTab("code")}
                    data-testid="switch-code"
                    className="text-xs text-zinc-500 hover:text-white uppercase tracking-widest font-bold"
                  >
                    Tenho um código de convite →
                  </button>
                </div>
              </div>
            )}

            {tab === "tecnico" && (
              <div className="mt-6">
                <p className="text-zinc-400 text-sm">
                  Acesso do técnico e comissão técnica com conta Google. O primeiro usuário do sistema é automaticamente técnico.
                </p>
                <Button
                  onClick={handleGoogle}
                  data-testid="google-login-btn"
                  className="w-full mt-6 bg-white hover:bg-zinc-100 text-black font-bold uppercase tracking-wide py-6 rounded-sm text-base"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                  </svg>
                  Entrar com Google
                </Button>
                <p className="text-[10px] text-zinc-600 mt-4 uppercase tracking-widest">
                  Restrito a técnicos e comissão técnica.
                </p>
              </div>
            )}

            {tab === "code" && (
              <div className="mt-6">
                <button
                  onClick={() => setTab("atleta")}
                  className="text-xs text-zinc-500 hover:text-white uppercase tracking-widest font-bold mb-4"
                  data-testid="back-to-atleta"
                >
                  ← Voltar
                </button>
                <form onSubmit={handleInvite} className="space-y-3">
                  <p className="text-zinc-400 text-sm">
                    Recebeu um código do seu técnico? Use-o para entrar — sem precisar de email.
                  </p>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Código</label>
                    <Input
                      data-testid="invite-code-input"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      placeholder="EX: A2B3CD"
                      maxLength={12}
                      className="mt-2 bg-[#0A0A0A] border-zinc-800 text-white text-2xl font-display font-bold uppercase tracking-[0.3em] text-center"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Seu Nome</label>
                    <Input
                      data-testid="invite-name-input"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      placeholder="Como devemos te chamar?"
                      className="mt-2 bg-[#0A0A0A] border-zinc-800 text-white"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="invite-submit-btn"
                    className="w-full bg-[#FF3B30] hover:bg-red-600 text-white font-bold uppercase tracking-wide py-6 rounded-sm text-base disabled:opacity-50"
                  >
                    {submitting ? "Validando..." : "Entrar"}
                  </Button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
